import { type AnchorHTMLAttributes, type MouseEvent, type ReactNode, useSyncExternalStore } from 'react';

// The pages are paths (/, /issues, /sources…); each page keeps its own state in the query string. Small on purpose:
// history, a hook that follows the path, and a link that does not reload the page.
const listeners = new Set<() => void>();
const notify = (): void => listeners.forEach((listener) => listener());
window.addEventListener('popstate', notify);

const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
};

export function navigate(to: string, options: { replace?: boolean } = {}): void {
    if (to === location.pathname + location.search) {
        return;
    }
    history[options.replace ? 'replaceState' : 'pushState'](null, '', to);
    notify();
}

export const usePath = (): string => useSyncExternalStore(subscribe, () => location.pathname);

// A plain link for the browser (open in a new tab works), a page change without a reload for a plain click.
export function Link({ to, onClick, ...rest }: { to: string } & AnchorHTMLAttributes<HTMLAnchorElement>): ReactNode {
    return (
        <a
            href={to}
            {...rest}
            onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                onClick?.(event);
                if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                    return;
                }
                event.preventDefault();
                navigate(to);
            }}
        />
    );
}
